import { Ref, useMemo } from "react";

import classNames from "classnames";

import { useTranslate } from "../../hooks/useTranslate";
import { ButtonCopy, ButtonCopyProps } from "../ButtonCopy/ButtonCopy";

import { TextFieldWithCopyWrapper } from "./TextFieldWithCopyWrapper";
import { useStyles } from "./useStyles";
import useTextFieldWithCopy, { TextFieldInputProps } from "./useTextFieldWithCopy";

import { DashboardLink } from "@/dashboard-design-system/Link";
import { BaseTextFieldProps, DashboardTextField } from "@/dashboard-design-system/TextField";

export function TextFieldWithCopy({
  ...props
}: TextFieldInputProps & BaseTextFieldProps & { buttonCopyProps?: Omit<ButtonCopyProps, "toCopy"> }) {
  const { t } = useTranslate();
  const classes = useStyles();
  const { handleSetInputRef, handleLinkCopy, disableChange, selectFullLink } = useTextFieldWithCopy(props);

  const InputProps = useMemo(
    () => ({
      className: classNames(classes.input, { [classes.multiline]: props.multiline }),
      ...props.InputProps,
    }),
    [classes.input, classes.multiline, props.multiline, props.InputProps]
  );

  return (
    <TextFieldWithCopyWrapper
      {...props}
      slots={{
        textFieldComponent: (
          <DashboardTextField
            {...props}
            ref={props.ref as Ref<HTMLInputElement>}
            inputRef={handleSetInputRef}
            data-test={props["data-test"] || "text-field-with-copy-icon"}
            endAdornment={
              !props.button && !props.copyButtonBelow ? (
                <DashboardLink vibe="brand" onClick={handleLinkCopy} className={classes.leftSpacing}>
                  {t("common", "copy")}
                </DashboardLink>
              ) : undefined
            }
            onChange={disableChange}
            onFocus={selectFullLink}
            value={props.value}
            size={props.size || "small"}
            InputProps={InputProps}
          />
        ),
        copyButtonComponent: (
          <ButtonCopy
            variant={props.buttonCopyProps?.variant || "filled"}
            vibe={props.buttonCopyProps?.vibe || "alternate"}
            size={props.buttonCopyProps?.size || "small"}
            toCopy={props.value}
            onClick={props.onCopy}
            amplitudeEvent={props.copyAmplitudeEvent}
            amplitudePayload={props.copyAmplitudePayload}
            copyText={props.buttonCopyProps?.copyText}
          />
        ),
      }}
    />
  );
}
